import { useState } from 'react';
import { BottomSheet, Button, Input } from './ui';
import { submitVenueClaim } from './venueDetailApi';
import './venue-claim-modal.css';

type VenueClaimModalProps = {
  venueId: string;
};

type ClaimUiState =
  | 'idle'
  | 'submitting'
  | 'submitted'
  | 'duplicate'
  | 'account-exists'
  | 'auth-required'
  | 'error';

type ContactErrors = {
  phone?: string;
  email?: string;
};

function claimMessage(state: ClaimUiState): string | null {
  switch (state) {
    case 'submitted':
      return 'Заявка отправлена. Команда Хоппера свяжется с вами по указанным контактам после ручной проверки. Доступ к кабинету не выдаётся автоматически.';
    case 'duplicate':
      return 'Вы уже отправляли заявку на это заведение. Повторная заявка с этого аккаунта недоступна.';
    case 'account-exists':
      return 'Для этого заведения уже существует активный кабинет.';
    case 'auth-required':
      return 'Чтобы отправить заявку, войдите в Хоппер и попробуйте снова.';
    case 'error':
      return 'Не удалось отправить заявку. Проверьте соединение и попробуйте снова.';
    default:
      return null;
  }
}

function validatePhone(value: string): string | undefined {
  const phone = value.trim();
  const digits = phone.replace(/\D/g, '');

  if (!phone) return 'Укажите номер телефона';
  if (!/^\+?[0-9\s().-]+$/.test(phone) || digits.length < 7 || digits.length > 15) {
    return 'Проверьте формат номера телефона';
  }

  return undefined;
}

function validateEmail(value: string): string | undefined {
  const email = value.trim();

  if (!email) return 'Укажите электронную почту';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Проверьте адрес электронной почты';
  }

  return undefined;
}

export function VenueClaimModal({ venueId }: VenueClaimModalProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<ClaimUiState>('idle');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<ContactErrors>({});

  const resetForm = () => {
    setState('idle');
    setPhone('');
    setEmail('');
    setErrors({});
  };

  const close = () => {
    setOpen(false);
    resetForm();
  };

  const openModal = () => {
    resetForm();
    setOpen(true);
  };

  const submit = async () => {
    if (state === 'submitting') return;

    const nextErrors: ContactErrors = {
      phone: validatePhone(phone),
      email: validateEmail(email),
    };

    if (nextErrors.phone || nextErrors.email) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setState('submitting');

    try {
      const result = await submitVenueClaim(venueId, phone.trim(), email.trim());

      switch (result.outcome) {
        case 'submitted':
          setState('submitted');
          return;
        case 'duplicate':
          setState('duplicate');
          return;
        case 'account-exists':
          setState('account-exists');
          return;
        case 'auth-required':
          setState('auth-required');
          return;
        case 'validation':
          setErrors({
            phone: result.fields.contact_phone,
            email: result.fields.contact_email,
          });
          setState('idle');
          return;
        default:
          setState('error');
      }
    } catch {
      setState('error');
    }
  };

  const message = claimMessage(state);
  const terminal = state === 'submitted' || state === 'duplicate' || state === 'account-exists';

  return (
    <>
      <div className="venue-claim-entry">
        <button className="venue-claim-entry__link" type="button" onClick={openModal}>
          Это моё заведение
        </button>
      </div>

      <BottomSheet
        open={open}
        onClose={close}
        title="Это моё заведение"
        className="venue-claim-modal"
        maxHeight="78dvh"
      >
        <div className="venue-claim-modal__content">
          <p>
            Если вы владелец или представитель этого заведения, отправьте заявку на доступ к кабинету.
            Команда Хоппера проверит её вручную и свяжется с вами для подтверждения прав.
          </p>

          {!terminal && (
            <div className="venue-claim-modal__fields">
              <Input
                label="Телефон"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                placeholder="+7 999 123-45-67"
                value={phone}
                error={errors.phone}
                disabled={state === 'submitting'}
                onChange={(event) => {
                  setPhone(event.target.value);
                  if (errors.phone) setErrors((current) => ({ ...current, phone: undefined }));
                }}
              />
              <Input
                label="Электронная почта"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="name@example.ru"
                value={email}
                error={errors.email}
                disabled={state === 'submitting'}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (errors.email) setErrors((current) => ({ ...current, email: undefined }));
                }}
              />
            </div>
          )}

          {!terminal && (
            <p className="venue-claim-modal__note">
              С одного аккаунта можно отправить только одну заявку на это заведение. Отправка заявки не даёт доступ к кабинету автоматически.
            </p>
          )}

          {message && (
            <p className="venue-claim-modal__status" role="status">
              {message}
            </p>
          )}

          {terminal ? (
            <Button className="venue-claim-modal__button" type="button" variant="secondary" onClick={close}>
              Закрыть
            </Button>
          ) : (
            <Button
              className="venue-claim-modal__button"
              type="button"
              variant="primary"
              disabled={state === 'submitting'}
              onClick={() => void submit()}
            >
              {state === 'submitting' ? 'Отправляем…' : 'Отправить заявку'}
            </Button>
          )}
        </div>
      </BottomSheet>
    </>
  );
}
